<template>
  <div class="v2-additional-info">
    <h3 class="v2-additional-info__section-title">
      Additional Info
    </h3>

    <!-- Row 1: Country of Origin + HS Code -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Country of Origin</label>
        <v2-dropdown
          :options="countryDropdownOptions"
          :value="country && country[0]"
          placeholder="Select country of origin"
          :searchable="true"
          @select="onSelectCountry"
        />
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">HS Code</label>
        <input
          v-model="hscode"
          class="v2-additional-info__input"
          placeholder="Enter HS Code"
        >
      </div>
    </div>

    <!-- Row 2: Shipping Start Date + Shipping End Date -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Shipping Start Date</label>
        <input
          :value="formatDateForInput(shippingStartDate)"
          type="date"
          class="v2-additional-info__input"
          @input="shippingStartDate = $event.target.value ? new Date($event.target.value) : null"
        >
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Shipping End Date</label>
        <input
          :value="formatDateForInput(shippingEndDate)"
          type="date"
          class="v2-additional-info__input"
          @input="shippingEndDate = $event.target.value ? new Date($event.target.value) : null"
        >
      </div>
    </div>

    <!-- Row 3: Lines + Sub Lines -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Lines</label>
        <input
          v-model="lines"
          class="v2-additional-info__input"
          placeholder="Enter Lines"
        >
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Sub Lines</label>
        <input
          v-model="subLines"
          class="v2-additional-info__input"
          placeholder="Enter Sub Lines"
        >
      </div>
    </div>

    <!-- Row 4: Groups + Sub Groups -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Groups</label>
        <input
          v-model="groups"
          class="v2-additional-info__input"
          placeholder="Enter Groups"
        >
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Sub Groups</label>
        <input
          v-model="subGroups"
          class="v2-additional-info__input"
          placeholder="Enter Sub Groups"
        >
      </div>
    </div>

    <!-- Row 5: Keywords + Key Styles -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Keywords (comma separated)</label>
        <div class="v2-additional-info__tags">
          <span
            v-for="(tag, index) in keywords"
            :key="index"
            class="v2-additional-info__tag"
          >
            {{ tag }}
            <button
              class="v2-additional-info__tag-remove"
              type="button"
              @click="removeKeyword(index)"
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                />
              </svg>
            </button>
          </span>
          <input
            v-model="keywordInput"
            class="v2-additional-info__tag-input"
            :placeholder="!keywords || keywords.length === 0 ? 'Add keywords (comma separated)' : ''"
            @keydown.enter.prevent="addKeyword"
            @input="onKeywordInput"
            @blur="addKeyword"
          >
        </div>
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Key Styles</label>
        <input
          v-model="keyStyles"
          class="v2-additional-info__input"
          placeholder="Enter Key Styles"
        >
      </div>
    </div>

    <!-- Row 6: Recommended Items + Product Order -->
    <div class="v2-additional-info__row">
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Recommended Items (comma separated)</label>
        <div class="v2-additional-info__tags">
          <span
            v-for="(tag, index) in recommendedItems"
            :key="index"
            class="v2-additional-info__tag"
          >
            {{ tag }}
            <button
              class="v2-additional-info__tag-remove"
              type="button"
              @click="removeRecommendedItem(index)"
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                />
              </svg>
            </button>
          </span>
          <input
            v-model="recommendedItemInput"
            class="v2-additional-info__tag-input"
            :placeholder="!recommendedItems || recommendedItems.length === 0 ? 'Add recommended items (comma separated)' : ''"
            @keydown.enter.prevent="addRecommendedItem"
            @input="onRecommendedItemInput"
            @blur="addRecommendedItem"
          >
        </div>
      </div>
      <div class="v2-additional-info__field">
        <label class="v2-additional-info__label">Product Order</label>
        <validation-provider
          #default="{ errors }"
          name="Product Order"
          rules="numeric"
        >
          <input
            v-model="orderIndex"
            class="v2-additional-info__input"
            :class="{ 'v2-additional-info__input--error': errors[0] }"
            type="text"
            placeholder="Enter Product Order"
            @keypress="onlyAllowPositiveInteger"
            @paste="blockInvalidIntegerPaste"
          >
          <span
            v-if="errors[0]"
            class="v2-additional-info__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { apiToastError } from '@/@core/utils/toast'
import {
  blockInvalidIntegerPaste,
  onlyAllowPositiveInteger,
} from '@/views/apps/product/edit/utils'
import { useAdditionalInfo } from '@/views/apps/product/edit/input-sections/hooks/useAdditionalInfo'
import { ValidationProvider } from 'vee-validate'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'

export default {
  name: 'V2AdditionalInfo',
  components: {
    ValidationProvider,
    V2Dropdown,
  },
  data() {
    return {
      allCountries: [],
      keywordInput: '',
      recommendedItemInput: '',
    }
  },
  computed: {
    countryDropdownOptions() {
      return this.allCountries.map(c => ({ label: c, value: c }))
    },
  },
  created() {
    this.getAllCountries()
  },
  methods: {
    onlyAllowPositiveInteger,
    blockInvalidIntegerPaste,
    formatDateForInput(date) {
      if (!date) return ''
      const d = new Date(date)
      if (isNaN(d.getTime())) return ''
      return d.toISOString().split('T')[0]
    },
    onSelectCountry(val) {
      this.country = val ? [val] : []
    },
    getAllCountries() {
      store.dispatch(GET_COUNTRIES).then(res => {
        this.allCountries = res.data.data.COUNTRIES
      }).catch(() => {
        apiToastError('Sorry, we couldn\'t load the list of countries. Please try again shortly.')
      })
    },
    _handleTagInput(inputField, addFn) {
      if (this[inputField].includes(',')) {
        addFn()
      }
    },
    _addTag(inputField, listField) {
      if (!this[inputField].trim()) return
      const newTags = this[inputField].split(',').map(t => t.trim()).filter(Boolean)
      const existing = this[listField] || []
      const unique = newTags.filter(t => !existing.includes(t))
      if (unique.length) {
        this[listField] = [...existing, ...unique]
      }
      this[inputField] = ''
    },
    _removeTag(listField, index) {
      const arr = [...(this[listField] || [])]
      arr.splice(index, 1)
      this[listField] = arr
    },
    onKeywordInput() {
      this._handleTagInput('keywordInput', this.addKeyword)
    },
    addKeyword() {
      this._addTag('keywordInput', 'keywords')
    },
    removeKeyword(index) {
      this._removeTag('keywords', index)
    },
    onRecommendedItemInput() {
      this._handleTagInput('recommendedItemInput', this.addRecommendedItem)
    },
    addRecommendedItem() {
      this._addTag('recommendedItemInput', 'recommendedItems')
    },
    removeRecommendedItem(index) {
      this._removeTag('recommendedItems', index)
    },
  },
  setup() {
    const additionalInfo = useAdditionalInfo()
    return { ...additionalInfo }
  },
}
</script>

<style lang="scss" scoped>
.v2-additional-info {
  margin-top: var(--v2-space-6);

  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-4);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__row {
    display: flex;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-3);

    > .v2-additional-info__field {
      flex: 1;
      min-width: 0;
    }
  }

  &__field {
    margin-bottom: var(--v2-space-2);
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-1);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  // Glass form field — matches V2Input recipe.
  &__input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    min-height: 38px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(:focus):not([readonly]) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__error {
    display: block;
    font-size: 0.6875rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }

  // Glass tag-input container.
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--v2-space-1);
    padding: var(--v2-space-1) var(--v2-space-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    background: var(--v2-glass-surface-2);
    min-height: 38px;
    align-items: center;

    &:focus-within {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    padding: 2px 8px;
    background: var(--v2-muted);
    border-radius: var(--v2-radius-sm);
    font-size: 0.75rem;
    color: var(--v2-foreground);
  }

  &__tag-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: var(--v2-muted-foreground);

    &:hover {
      color: var(--v2-destructive);
    }
  }

  &__tag-input {
    flex: 1;
    min-width: 100px;
    border: none;
    outline: none;
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: transparent;
    padding: var(--v2-space-1) 0;
  }
}
</style>
